import React from 'react';
import type { RoutePath } from '@shared/core/router.helper/useRouterHelper';
import loadable from '@loadable/component';
import { RoutesProvider } from '@shared/core';
import { CREATE_WEDDING_APP_NAME } from '@apps/createWedding/constants';
import { CREATE_BABY_REGISTRY_APP_NAME } from '@apps/createBabyRegistry/constants';
import { SERVICE_CENTER_APP_NAME } from '@apps/serviceCenter/constants';
import { EventRoute } from './EventRoute';
import { getEcardType } from '@apps/saveTheDate/SaveTheDate.utils';
import EcardError from '@shared/components/EmailsAndEcards/components/Error';
import { SAVE_THE_DATE_PHOTO_ECARD_TYPE } from '@apps/saveTheDate/constants';
import { CardsRoutesProvider } from '@apps/card/Card.routes';

const Admin = loadable(
  () =>
    import(
      /* webpackChunkName: "content/admin" */
      '@apps/admin'
    ),
);

const Guest = loadable(
  () =>
    import(
      /* webpackChunkName: "content/guest" */
      '@apps/guest'
    ),
);

const AccountApp = loadable(
  () =>
    import(
      /* webpackChunkName: "content/account" */
      '@apps/account'
    ),
);

const PublicShop = loadable(
  () =>
    import(
      /* webpackChunkName: "content/publicshop" */
      '@apps/registry/public/routes/Shop/Shop'
    ),
);

const AccountMe = loadable(
  () =>
    import(
      /* webpackChunkName: "content/account/me" */
      '@apps/accountMe'
    ),
);

const CreateWedding = loadable(
  () =>
    import(
      /* webpackChunkName: "content/createwedding" */
      '@apps/createWedding'
    ),
);

const CreateBabyRegistry = loadable(
  () =>
    import(
      /* webpackChunkName: "content/createbabyregistry" */
      '@apps/createBabyRegistry'
    ),
);

const EventRedirect = loadable(
  () =>
    import(
      /* webpackChunkName: "content/eventredirect" */
      '@apps/admin/routes/EventRedirect'
    ),
);

const ShopComponent = () => {
  return (
    <RoutesProvider>
      <PublicShop />
    </RoutesProvider>
  );
};

const EcardGuest = loadable(
  () =>
    import(
      /* webpackChunkName: "content/ecard/guest" */
      '@apps/ecard/guest'
    ),
);

const SaveTheDateGuest = loadable(
  () =>
    import(
      /* webpackChunkName: "content/saveTheDate/guest" */
      '@apps/saveTheDate/guest'
    ),
);

const ServiceCenter = loadable(
  () =>
    import(
      /* webpackChunkName: "content/servicecenter" */
      '@apps/serviceCenter'
    ),
);

const ContactCollectorGuest = loadable(
  () =>
    import(
      /* webpackChunkName: "content/contactCollector/guest" */
      '@apps/contactCollector/guest'
    ),
);

const PublicStationeryGallery = loadable(
  () =>
    import(
      /* webpackChunkName: "content/publicstationeryshop" */
      '@apps/card/routes/PublicShop'
    ),
);

const RecipientPremiumEcardView = loadable(
  () =>
    /* webpackChunkName: "content/card/recipientpremiumecardpreview" */
    import('@apps/card/routes/RecipientPremiumEcardView'),
);

const ContactCollectorGuestWrapper = ({ magicLinkId }: { magicLinkId: string }) => {
  return (
    <RoutesProvider>
      <ContactCollectorGuest magicLinkId={magicLinkId} />
    </RoutesProvider>
  );
};

const RegistryGuest = loadable(
  () =>
    import(
      /* webpackChunkName: "content/registry-guest" */
      '@apps/registry/guest'
    ),
);

const CreateGeneralEvent = loadable(() => import('@apps/createGeneralEvent'));

const PageNotFound = loadable(() => import(/* webpackChunkName: "apps-pageNotFound-ssr" */ '@apps/pageNotFound/ssr'));

const PublicStaysApp = loadable(
  () =>
    import(
      /* webpackChunkName: "content/hotels" */
      '@apps/stays/PublicStays'
    ),
);

export const ssrRoutes: Array<RoutePath<{ eventHandle: string; magicLinkId: string; ecardId: string }>> = [
  {
    path: '/404',
    component: () => <PageNotFound />,
  },
  {
    path: '/contact/:magicLinkId',
    component: ({ match }) => (match ? <ContactCollectorGuestWrapper magicLinkId={match.params.magicLinkId} /> : null),
  },
  {
    path: '/account/(events|personal-info)',
    component: () => <AccountMe appUrl={'account'} />,
  },
  {
    path: '/account',
    component: () => <AccountApp appUrl="account" />,
  },
  {
    path: '/shipping-policy',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    path: '/return-policy',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    path: '/card/:ecardId(\\w{10})',
    component: ({ match }) => (
      <CardsRoutesProvider>
        <RecipientPremiumEcardView orderNumber={match.params.ecardId} />
      </CardsRoutesProvider>
    ),
  },
  {
    path: '/cards',
    component: () => <PublicStationeryGallery />,
  },
  {
    path: '/shop',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    path: `/${CREATE_WEDDING_APP_NAME}`,
    component: () => <CreateWedding />,
  },
  {
    path: `/${CREATE_BABY_REGISTRY_APP_NAME}`,
    component: () => <CreateBabyRegistry />,
  },
  {
    path: '/ecard',
    component: ({ location }) => {
      const ecardType = getEcardType(location);
      return ecardType === undefined ? <EcardError /> : ecardType === SAVE_THE_DATE_PHOTO_ECARD_TYPE ? <SaveTheDateGuest /> : <EcardGuest />;
    },
  },
  {
    path: '/createevent',
    component: () => <CreateGeneralEvent />,
  },
  {
    path: '/event',
    component: () => <EventRedirect />,
  },
  {
    path: '/travel',
    component: () => <PublicStaysApp />,
  },
  {
    path: `/${SERVICE_CENTER_APP_NAME}`,
    component: () => <ServiceCenter />,
  },
  {
    path: '/:eventHandle/edit',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={Admin}
      />
    ),
  },
  {
    path: '/:eventHandle/registry',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={RegistryGuest}
      />
    ),
  },
  {
    path: '/:eventHandle',
    component: ({ match }) => (
      <EventRoute eventHandle={match.params.eventHandle}>
        <Guest />
      </EventRoute>
    ),
  },
];
